import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { Pagination } from "swiper/modules";
import { CarouselSlide, SlideType } from "./slide";
import { ContentCard } from "../content-card";
import classNames from "classnames";
import { PaginationOptions } from "swiper/types";

import "swiper/css";
import "swiper/css/pagination";
import styles from "./styles.modules.scss";
import { useSnowplowContext } from "../../analytics/snowplow";
import { saveContentfulItems } from "../../utils/save-contentful-items";
import { GoldenRippleSlide } from "./slide/golden-ripple";
import { PollSlideType } from "./slide/poll";
import { MangoParadoxProgressSlide } from "./slide/mango-paradox";
import { ProgressSlideType } from "./slide/progress";

type CarouselProps = {
    locale: {
        lang?: string;
    };
    Slide: typeof CarouselSlide;
    slides: SlideType[];
    onSlideChange?: (index: number) => void;
};

function iosMajorVersion() {
    if (/iP(hone|od|ad)/.test(navigator.platform)) {
        const v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);

        const majorVersion = v && v[1] ? parseInt(v[1], 10) : null;

        return majorVersion;
    }

    return null;
}

export const SwiperCarousel = ({
    locale,
    Slide,
    slides,
    onSlideChange,
}: CarouselProps) => {
    const { trackImpression } = useSnowplowContext();
    const [searchParams] = useSearchParams();

    const useiOS12WorkAround = (iosMajorVersion() ?? Infinity) <= 12;

    const [time, setTime] = React.useState(Date.now());
    const lastAnalyticsId = React.useRef<string>("");

    const hasTimerLabel = slides.some((slide) => {
        const hasTimer = slide.type === "eventEntry";
        return hasTimer && slide.timerLabel;
    });

    saveContentfulItems("slides", slides);

    useEffect(() => {
        if (hasTimerLabel) {
            const intervalId = setInterval(() => {
                const currentTime = Date.now();
                setTime(currentTime);
            }, 1000);

            const clear = () => {
                clearInterval(intervalId);
            };

            return clear;
        }
    }, []);

    const filteredSlides = slides.filter((slide) => {
        if (
            slide.type === "pollEntry" &&
            slide.targeting?.campaignId === "golden-ripple"
        ) {
            return false;
        }

        const hasTimer = slide.type === "eventEntry";

        if (!hasTimer || !slide.timerKind) {
            return true;
        }

        if (slide.timerKind === "HIDE_ENTRY") {
            const shouldShow = slide.timerTarget > time;

            return shouldShow;
        }

        return true;
    });

    const paginationProps: PaginationOptions = {
        bulletActiveClass: classNames("selected", styles.selected),
        bulletClass: classNames("page", styles.page),
        enabled: filteredSlides.length > 1,
    };

    const sendAnalytics = (slideIndex: number) => {
        const currentSlide = filteredSlides[slideIndex];
        if (currentSlide && currentSlide.id !== lastAnalyticsId.current) {
            trackImpression(currentSlide.id, currentSlide.title);
            lastAnalyticsId.current = currentSlide.id;
        }
    };

    const goldenRipplePollSlide = slides.find(
        (slide) =>
            slide.type === "pollEntry" &&
            slide.targeting?.campaignId === "golden-ripple",
    );

    const mangoParadoxLatestDayNumber = filteredSlides.reduce((max, slide) => {
        if (
            slide.type === "progressTrackerEntry" &&
            slide.targeting?.campaignId === "mango-paradox"
        ) {
            const dayNumber =
                slide.relatedMedia?.find(
                    (media) => media.title === "day-number",
                )?.label !== undefined
                    ? Number(
                          slide.relatedMedia?.find(
                              (media) => media.title === "day-number",
                          )?.label,
                      )
                    : null;
            if (dayNumber && dayNumber > max) {
                return dayNumber;
            }
        }
        return max;
    }, 0);

    const mangoParadoxSortedSlides = filteredSlides
        .filter(
            (slide) =>
                slide.type === "progressTrackerEntry" &&
                slide.targeting?.campaignId === "mango-paradox",
        )
        .sort((a, b) => {
            const aDayNumber =
                a.type === "progressTrackerEntry" &&
                a.targeting?.campaignId === "mango-paradox"
                    ? Number(
                          a.relatedMedia?.find(
                              (media) => media.title === "day-number",
                          )?.label,
                      )
                    : 0;
            const bDayNumber =
                b.type === "progressTrackerEntry" &&
                b.targeting?.campaignId === "mango-paradox"
                    ? Number(
                          b.relatedMedia?.find(
                              (media) => media.title === "day-number",
                          )?.label,
                      )
                    : 0;

            return aDayNumber - bDayNumber;
        });

    let mangoParadoxAllRewards: { [key: number]: (string | undefined)[] } = {};
    mangoParadoxSortedSlides.forEach((slide) => {
        if (slide.type !== "progressTrackerEntry") {
            return null;
        }

        const dayNumber = Number(
            slide.relatedMedia?.find((media) => media.title === "day-number")
                ?.label ?? 0,
        );

        const reward1 = slide.relatedMedia?.find(
            (media) => media.title === "reward-1",
        )?.image?.url;

        const reward2 = slide.relatedMedia?.find(
            (media) => media.title === "reward-2",
        )?.image?.url;

        const reward3 = slide.relatedMedia?.find(
            (media) => media.title === "reward-3",
        )?.image?.url;

        mangoParadoxAllRewards[dayNumber] = [reward1, reward2, reward3];
    });

    let mangoParadoxPickedSpellFactoryRewards: {
        [key: number]: string | undefined;
    } = {};

    let mangoParadoxPickedRewards: { [key: number]: number | undefined } = {};
    mangoParadoxSortedSlides.forEach((slide) => {
        if (slide.type !== "progressTrackerEntry") {
            return null;
        }

        const dayNumber = Number(
            slide.relatedMedia?.find((media) => media.title === "day-number")
                ?.label ?? 0,
        );

        const previousDayPickedReward = slide.relatedMedia?.find(
            (media) => media.title === "previous-day-picked-reward",
        )?.label;

        const previousDaySpellFactoryReward = slide.relatedMedia?.find(
            (media) => media.title === "previous-day-spell-factory-reward",
        )?.label;

        if (dayNumber === 1) return;

        // -1 for previous day
        mangoParadoxPickedRewards[dayNumber - 1] =
            previousDayPickedReward === undefined
                ? undefined
                : Number(previousDayPickedReward);

        mangoParadoxPickedSpellFactoryRewards[dayNumber - 1] =
            previousDaySpellFactoryReward;
    });

    return (
        <Swiper
            onSlideChange={(swiper) => {
                sendAnalytics(swiper.realIndex);
                if (onSlideChange) {
                    onSlideChange(swiper.realIndex);
                }
            }}
            onAfterInit={() => {
                // onSlideChange captures all the other carousel slide views, except the first one which we track here
                sendAnalytics(0);
            }}
            onSwiper={(swiper) => {
                const entryId = searchParams.get("openCarouselId");

                const possibleSlideIndex = filteredSlides.findIndex(
                    (slide) => slide.id === entryId,
                );

                if (possibleSlideIndex > -1) {
                    swiper.slideTo(possibleSlideIndex);
                }
            }}
            loop={true}
            pagination={paginationProps}
            modules={[Pagination]}
            className={classNames(styles.carousel, "carousel", {
                single: filteredSlides.length === 1,
                multi: filteredSlides.length > 1,
            })}
            onPaginationRender={(_, el) => {
                // There's no option for the container class name, so we use this
                el.classList.add("paginator");
                el.classList.add(styles.paginator ?? "");
            }}
        >
            {filteredSlides.map((slide) => {
                if (
                    slide.type === "progressTrackerEntry" &&
                    slide.targeting?.campaignId === "mango-paradox" &&
                    slide.relatedMedia?.find(
                        (media) => media.title === "day-number",
                    )?.label !== mangoParadoxLatestDayNumber.toString()
                ) {
                    return null; // Skip rendering this slide
                }

                return (
                    <SwiperSlide key={slide.id} className="slide">
                        {() => {
                            const swiper = useSwiper();

                            return (
                                <div
                                    className={`${
                                        slide.targeting?.campaignId
                                            ? slide.targeting.campaignId
                                            : ""
                                    }`}
                                    onClick={(e) => {
                                        if (!useiOS12WorkAround) {
                                            return;
                                        }

                                        if (
                                            (e.target as HTMLInputElement)
                                                .nodeName !== "DIV"
                                        ) {
                                            return;
                                        }

                                        swiper.slideNext();
                                    }}
                                >
                                    <ContentCard
                                        id={slide.id}
                                        lang={locale.lang}
                                    >
                                        {slide.type === "eventEntry" &&
                                        slide.targeting?.campaignId ===
                                            "golden-ripple" ? (
                                            <GoldenRippleSlide
                                                slide={{
                                                    ...slide,
                                                    lang: locale.lang || "en",
                                                }}
                                                pollSlide={
                                                    goldenRipplePollSlide as PollSlideType
                                                }
                                            />
                                        ) : slide.type ===
                                              "progressTrackerEntry" &&
                                          slide.targeting?.campaignId ===
                                              "mango-paradox" ? (
                                            <MangoParadoxProgressSlide
                                                slide={slide}
                                                previousDaySlide={(() => {
                                                    const previousDaySlideId =
                                                        slide.relatedMedia?.find(
                                                            (media) =>
                                                                media.title ===
                                                                "previous-day-id",
                                                        )?.label;

                                                    const previousDaySlide =
                                                        filteredSlides.find(
                                                            (s) =>
                                                                s.id ===
                                                                previousDaySlideId,
                                                        ) as ProgressSlideType;

                                                    return previousDaySlide;
                                                })()}
                                                allRewards={
                                                    mangoParadoxAllRewards
                                                }
                                                pickedRewards={
                                                    mangoParadoxPickedRewards
                                                }
                                                pickedSpellFactoryRewards={
                                                    mangoParadoxPickedSpellFactoryRewards
                                                }
                                            />
                                        ) : (
                                            <Slide {...slide} />
                                        )}
                                    </ContentCard>
                                </div>
                            );
                        }}
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
};
